export const BOOTSTRAP_BREAKPOINTS = {
  SMALL: 'SMALL',
  MEDIUM: 'MEDIUM',
  LARGE: 'LARGE',
  EXTRA_LARGE: 'EXTRA_LARGE',
};

const BOOTSTRAP_BREAKPOINT_MEDIA_QUERIES = {
  SMALL: '(min-width: 576px)',
  MEDIUM: '(min-width: 768px)',
  LARGE: '(min-width: 992px)',
  EXTRA_LARGE: '(min-width: 1200px)',
};

/**
 * Given a bootstrap breakpoint, will return true if it matches. For example:
 *
 *    cheakBreakpoint("SMALL") will return true if the window width is at least
 *    576px and false otherwise.
 */
export function checkBreakpoint(bootstrapBreakpoint) {
  if (!BOOTSTRAP_BREAKPOINT_MEDIA_QUERIES[bootstrapBreakpoint]) {
    return undefined;
  }

  const mq = window.matchMedia(BOOTSTRAP_BREAKPOINT_MEDIA_QUERIES[bootstrapBreakpoint]);

  if (mq.matches) {
    return true;
  } else {
    return false;
  }
}

export function getBreakpoints() {
  return {
    isSmall: checkBreakpoint(BOOTSTRAP_BREAKPOINTS.SMALL),
    isMedium: checkBreakpoint(BOOTSTRAP_BREAKPOINTS.MEDIUM),
    isLarge: checkBreakpoint(BOOTSTRAP_BREAKPOINTS.LARGE),
    isXLarge: checkBreakpoint(BOOTSTRAP_BREAKPOINTS.EXTRA_LARGE),
  };
}

/**
 * Compares current form values with original values to determine if the form is pristine
 * @param {Object} values - Formik form values (current state)
 * @param {Object} originalPreferences - Stashed initial state of Formik values
 * @param {Object} passThroughConditions - Optional object to ignore specific changed values
 * @returns {boolean} True if form values match original values, false otherwise
 */
export function isPristine(values, originalPreferences, passThroughConditions = {}) {
  const original = originalPreferences ?? {};

  const customFieldMappings = generateFieldMappings(values);

  if (Object.keys(customFieldMappings).length <= 0) {
    return true;
  }

  if ((!values || Object.keys(values).length === 0) && (!original || Object.keys(original).length === 0)) {
    return true;
  }

  const normalize = (val) => {
    if (Array.isArray(val)) return val.map(normalize);
    if (val && typeof val === 'object' && 'value' in val) return val.value;
    return val;
  };

  const areEqual = (a, b, key, passThroughConditions = {}) => {
    const bothEmpty = isValueEmpty(a, key, passThroughConditions) && isValueEmpty(b, key, passThroughConditions);
    if (bothEmpty) return true;

    if (Array.isArray(a) && Array.isArray(b)) {
      if (a.length !== b.length) return false;

      const aCopy = [...a];
      const bCopy = [...b];

      for (let i = 0; i < aCopy.length; i++) {
        const itemA = aCopy[i];
        let foundMatch = false;

        for (let j = 0; j < bCopy.length; j++) {
          if (areEqual(itemA, bCopy[j], key, passThroughConditions)) {
            foundMatch = true;
            bCopy.splice(j, 1);
            break;
          }
        }

        if (!foundMatch) {
          return false;
        }
      }
      return bCopy.length === 0;
    }
    if (typeof a === 'object' && typeof b === 'object' && a && b) {
      const aClean = stripEmptyProps(a);
      const bClean = stripEmptyProps(b);

      const aKeys = Object.keys(aClean);
      const bKeys = Object.keys(bClean);

      if (aKeys.length !== bKeys.length) return false;

      return aKeys.every((k) => areEqual(aClean[k], bClean[k], `${key}.${k}`, passThroughConditions));
    }

    if (typeof a === 'string' && typeof b === 'string') {
      const aParsed = safeJsonParse(a);
      const bParsed = safeJsonParse(b);

      if (aParsed && bParsed && typeof aParsed === 'object' && typeof bParsed === 'object') {
        const aClean = stripEmptyProps(aParsed);
        const bClean = stripEmptyProps(bParsed);

        const aKeys = Object.keys(aClean);
        const bKeys = Object.keys(bClean);

        if (aKeys.length === 0 && bKeys.length === 0) return a === b;
        if (aKeys.length !== bKeys.length) return false;

        return aKeys.every((k) => areEqual(aClean[k], bClean[k], `${key}.${k}`, passThroughConditions));
      }
    }

    if (typeof a === 'number' || typeof b === 'number' || typeof a === 'string' || typeof b === 'string') {
      const aNum = typeof a === 'string' ? parseFloat(a) : a;
      const bNum = typeof b === 'string' ? parseFloat(b) : b;

      if (!isNaN(aNum) && !isNaN(bNum)) {
        return aNum === bNum;
      }
      return String(a) === String(b);
    }

    return a === b;
  };

  const isValueEmpty = (val, key, passThroughConditions = {}) => {
    if (key && key in passThroughConditions) {
      const condition = passThroughConditions[key];
      if (typeof condition === 'function') {
        if (condition(val)) return true;
      } else if (Array.isArray(condition)) {
        if (condition.includes(val)) return true;
      }
    }
    if (val === undefined || val === null || val === '') return true;
    if (Array.isArray(val)) return val.length === 0;
    if (typeof val === 'object' && val !== null) {
      if (Object.keys(val).length === 0) {
        return false;
      } else {
        return Object.entries(val).every(([k, v]) => isValueEmpty(v, k, passThroughConditions));
      }
    }
    return false;
  };

  for (const field in customFieldMappings) {
    const path = customFieldMappings[field];
    const key = path.length === 2 ? `${path[0]}.${path[1]}` : `${path[0]}`;

    const valueInValues = path.length === 2 ? values?.[path[0]]?.[path[1]] : values?.[path[0]];
    const valueInOriginal = path.length === 2 ? original?.[path[0]]?.[path[1]] : original?.[path[0]];

    let normValue = normalize(valueInValues);
    let normOriginal = normalize(valueInOriginal);

    normValue = cleanIfAllEmptyProps(normValue);
    normOriginal = cleanIfAllEmptyProps(normOriginal);

    if (isValueEmpty(normValue, key, passThroughConditions) && isValueEmpty(normOriginal, key, passThroughConditions)) {
      continue;
    }

    if (!areEqual(normValue, normOriginal, key, passThroughConditions)) {
      return false;
    }
  }

  return true;
}

function stripEmptyProps(obj) {
  if (typeof obj !== 'object' || obj === null) return obj;

  return Object.fromEntries(
    Object.entries(obj).filter(([_, value]) => value !== null && value !== undefined && value !== ''),
  );
}

function safeJsonParse(value) {
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
}

function cleanIfAllEmptyProps(value) {
  if (Array.isArray(value)) {
    const cleanedArray = value.map(cleanIfAllEmptyProps).filter((v) => v !== null && v !== undefined);

    return cleanedArray.length > 0 ? cleanedArray : undefined;
  }

  if (value && typeof value === 'object') {
    if (value instanceof Date) {
      return value;
    }
    const cleanedObject = Object.fromEntries(
      Object.entries(value)
        .map(([k, v]) => [k, cleanIfAllEmptyProps(v)])
        .filter(([_, v]) => v !== null && v !== undefined),
    );

    return Object.keys(cleanedObject).length > 0 ? cleanedObject : undefined;
  }

  return value;
}
function generateFieldMappings(values) {
  const mappings = {};

  for (const section in values) {
    const value = values[section];

    if (value && typeof value === 'object' && !Array.isArray(value)) {
      if (Object.keys(value).length === 0) {
        mappings[section] = [section];
      } else {
        for (const key in value) {
          const path = `${section}.${key}`;
          mappings[path] = [section, key];
        }
      }
    } else {
      mappings[section] = [section];
    }
  }

  return mappings;
}
